import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Upload, RefreshCw, Trash2, Download, CheckCircle2, AlertTriangle, Flag, Search, Plus, X, RotateCcw, FileText } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA' };
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLE = {
  matched: { label: 'Ready to receipt', color: C.blue },
  unmatched: { label: 'No match', color: C.slate },
  part_receipted: { label: 'Part receipted', color: C.amber },
  receipted: { label: 'Receipted', color: C.green },
  exception: { label: 'Flagged', color: C.red },
  ignored: { label: 'Duplicate — ignored', color: C.slate },
};

function Stat({ label, value, sub, color, testid }) {
  return (
    <div className="px-4 py-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{label}</p>
      <p className="text-lg font-semibold font-mono" style={{ color: color || C.navy }}>{value}</p>
      {sub && <p className="text-[11px]" style={{ color: C.slate }}>{sub}</p>}
    </div>
  );
}

function UploadCard({ api, onLoaded }) {
  const [file, setFile] = useState(null);
  const [account, setAccount] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    form.append('account', account);
    try {
      const r = await api.post('/finance/bank/statements', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const s = r.data.statement;
      toast.success(`${s.credit_count} credits read · ${r.data.matched} matched to an open invoice`);
      onLoaded(s.statement_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The file could not be loaded');
    } finally {
      setBusy(false);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.16)' }} data-testid="bank-upload-card">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0" style={{ background: C.blue + '12' }}>
          <Upload className="w-4 h-4" style={{ color: C.blue }} />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Load a bank statement</h3>
          <p className="text-[11px] mt-0.5 mb-3" style={{ color: C.slate }}>
            CBA transaction export or any headered CSV, and OFX/QFX. Credits are matched against open
            invoices by reference, amount and payer name. Nothing is receipted until you confirm it.
            The same file, and the same transaction inside an overlapping file, are both refused.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input type="file" accept=".csv,.ofx,.qfx,.txt" data-testid="bank-upload-input" ref={inputRef}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-[11px] file:mr-2 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:text-[11px] file:font-medium file:bg-[#1A4C7C] file:text-white hover:file:bg-[#14406B] file:cursor-pointer" />
            <input value={account} onChange={(e) => setAccount(e.target.value)} placeholder="Account label (optional)"
              data-testid="bank-account-input"
              className="text-[11px] px-2 py-1.5 rounded-sm border" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
            <button onClick={submit} disabled={!file || busy} data-testid="bank-upload-btn"
              className="text-[11px] font-semibold px-3 py-1.5 rounded-sm text-white disabled:opacity-40"
              style={{ background: C.navy }}>
              {busy ? 'Reading…' : 'Load statement'}
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function CandidateRow({ cand, selected, amount, onToggle, onAmount, idx, lineId }) {
  const tierColor = cand.tier === 'exact' ? C.green : cand.tier === 'probable' ? C.blue : C.amber;
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }}
      data-testid={`bank-candidate-${lineId}-${idx}`}>
      <input type="checkbox" checked={selected} onChange={onToggle} data-testid={`bank-candidate-check-${lineId}-${idx}`} />
      <div className="min-w-[220px] flex-1">
        <p className="text-[12px] font-medium" style={{ color: C.navy }}>
          {cand.invoice_number} · {cand.business_name || '—'}
        </p>
        <p className="text-[10px]" style={{ color: C.slate }}>
          owes <span className="font-mono">{money(cand.due)}</span>
          {cand.policy_number ? ` · ${cand.policy_number}` : ''}
          {cand.due_date ? ` · due ${cand.due_date}` : ''}
        </p>
      </div>
      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase whitespace-nowrap"
        style={{ background: tierColor + '15', color: tierColor }}>
        {cand.tier} {cand.confidence}%
      </span>
      <input type="number" step="0.01" value={amount} disabled={!selected}
        onChange={(e) => onAmount(e.target.value)} data-testid={`bank-alloc-amount-${lineId}-${idx}`}
        className="w-28 text-[11px] font-mono px-2 py-1 rounded-sm border text-right disabled:opacity-40"
        style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
      <p className="basis-full text-[10px] pl-6" style={{ color: C.slate }}>{(cand.basis || []).join(' · ')}</p>
    </div>
  );
}

function ManualSearch({ api, lineId, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const run = async () => {
    try {
      const r = await api.get(`/finance/bank/invoices?q=${encodeURIComponent(q)}`);
      setRows(r.data.invoices || []);
      setOpen(true);
    } catch { toast.error('Invoice search failed'); }
  };

  return (
    <div className="pt-2">
      <div className="flex items-center gap-2">
        <Search className="w-3 h-3" style={{ color: C.slate }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run()}
          placeholder="Find another open invoice by number, insured or broker"
          data-testid={`bank-invoice-search-${lineId}`}
          className="flex-1 text-[11px] px-2 py-1 rounded-sm border" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
        <button onClick={run} data-testid={`bank-invoice-search-btn-${lineId}`}
          className="text-[11px] px-2 py-1 rounded-sm border font-medium"
          style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>Search</button>
      </div>
      {open && (
        <div className="mt-1 max-h-40 overflow-auto rounded-sm border" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
          {rows.length === 0 && <p className="text-[11px] p-2" style={{ color: C.slate }}>No open invoice matches that.</p>}
          {rows.map((inv, i) => (
            <button key={inv.invoice_id} onClick={() => { onPick(inv); setOpen(false); }}
              data-testid={`bank-invoice-pick-${lineId}-${i}`}
              className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-[#F5F7FA] flex items-center gap-2">
              <Plus className="w-3 h-3" style={{ color: C.blue }} />
              <span style={{ color: C.navy }}>{inv.invoice_number}</span>
              <span style={{ color: C.slate }}>{inv.business_name}</span>
              <span className="ml-auto font-mono" style={{ color: C.navy }}>{money(inv.due)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LineRow({ api, line, onChanged }) {
  const [open, setOpen] = useState(false);
  const [cands, setCands] = useState(line.candidates || []);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [flagging, setFlagging] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => { setCands(line.candidates || []); }, [line.candidates]);

  const style = STATUS_STYLE[line.status] || STATUS_STYLE.unmatched;
  const residual = Math.round((line.amount - (line.allocated_total || 0)) * 100) / 100;
  const allocatable = ['unmatched', 'matched', 'part_receipted', 'exception'].includes(line.status);

  const toggle = (id, suggested) => setSel(p => {
    const next = { ...p };
    if (next[id] !== undefined) delete next[id];
    else next[id] = String(Math.min(suggested, residual).toFixed(2));
    return next;
  });

  const confirm = async () => {
    const allocations = Object.entries(sel)
      .map(([invoice_id, amount]) => ({ invoice_id, amount: parseFloat(amount) }))
      .filter(a => a.amount > 0);
    if (!allocations.length) { toast.error('Tick an invoice and set the amount to receipt'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/finance/bank/lines/${line.line_id}/receipt`, { allocations });
      const n = (r.data.recorded || []).length;
      toast.success(`Receipt recorded against ${n} invoice${n === 1 ? '' : 's'}`);
      setSel({}); setOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The receipt was refused');
    } finally { setBusy(false); }
  };

  const flag = async () => {
    setBusy(true);
    try {
      await api.post(`/finance/bank/lines/${line.line_id}/flag`, { reason });
      toast.success('Credit flagged for review');
      setFlagging(false); setReason(''); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not flag this credit');
    } finally { setBusy(false); }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await api.post(`/finance/bank/lines/${line.line_id}/reopen`);
      toast.success('Credit reopened and rematched');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reopen this credit');
    } finally { setBusy(false); }
  };

  const best = cands[0];

  return (
    <>
      <tr className="border-t hover:bg-[#FAFBFC]" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`bank-line-${line.line_id}`}>
        <td className="px-3 py-2 text-[11px] font-mono whitespace-nowrap" style={{ color: C.slate }}>{line.posted_date}</td>
        <td className="px-3 py-2 text-[11px]" style={{ color: C.navy }}>
          <span className="block max-w-[340px] truncate" title={line.description}>{line.description || '—'}</span>
          {line.reference && <span className="text-[10px] font-mono" style={{ color: C.teal }}>{line.reference}</span>}
        </td>
        <td className="px-3 py-2 text-[11px] font-mono text-right whitespace-nowrap" style={{ color: C.navy }}>{money(line.amount)}</td>
        <td className="px-3 py-2 text-[11px]">
          {best ? (
            <span style={{ color: C.navy }}>
              {best.invoice_number}
              <span className="ml-1 text-[10px] font-semibold px-1 py-0.5 rounded"
                style={{ background: (best.tier === 'exact' ? C.green : C.blue) + '15', color: best.tier === 'exact' ? C.green : C.blue }}>
                {best.confidence}%
              </span>
            </span>
          ) : <span style={{ color: C.slate }}>—</span>}
        </td>
        <td className="px-3 py-2">
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase whitespace-nowrap"
            style={{ background: style.color + '15', color: style.color }} data-testid={`bank-line-status-${line.line_id}`}>
            {style.label}
          </span>
          {(line.allocated_total || 0) > 0 && (
            <span className="block text-[10px] font-mono mt-0.5" style={{ color: C.slate }}>
              {money(line.allocated_total)} allocated
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          {allocatable && (
            <button onClick={() => setOpen(o => !o)} data-testid={`bank-line-expand-${line.line_id}`}
              className="text-[11px] font-medium px-2 py-1 rounded-sm border"
              style={{ borderColor: C.blue + '55', color: C.blue }}>
              {open ? 'Close' : line.status === 'matched' ? 'Review & receipt' : 'Allocate'}
            </button>
          )}
          {line.status === 'exception' && (
            <button onClick={reopen} disabled={busy} data-testid={`bank-reopen-${line.line_id}`}
              className="ml-1 text-[11px] px-2 py-1 rounded-sm border inline-flex items-center gap-1"
              style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.slate }}>
              <RotateCcw className="w-3 h-3" /> Reopen
            </button>
          )}
          {line.status === 'receipted' && <CheckCircle2 className="w-4 h-4 inline" style={{ color: C.green }} />}
        </td>
      </tr>
      {(line.review || line.exception_reason) && (
        <tr style={{ background: (line.exception_reason ? C.red : C.amber) + '08' }}>
          <td />
          <td colSpan={5} className="px-3 pb-2 text-[11px]" style={{ color: line.exception_reason ? C.red : C.amber }}>
            <AlertTriangle className="w-3 h-3 inline mr-1" />{line.exception_reason || line.review}
          </td>
        </tr>
      )}
      {(line.allocations || []).length > 0 && (
        <tr style={{ background: C.green + '06' }}>
          <td />
          <td colSpan={5} className="px-3 pb-2 text-[11px]" style={{ color: C.slate }}>
            {line.allocations.map((a, i) => (
              <span key={i} className="mr-3" data-testid={`bank-allocation-${line.line_id}-${i}`}>
                <CheckCircle2 className="w-3 h-3 inline mr-1" style={{ color: C.green }} />
                {money(a.amount)} → {a.invoice_number} ({a.business_name}) · invoice now {a.invoice_status_after}
              </span>
            ))}
          </td>
        </tr>
      )}
      {open && (
        <tr style={{ background: C.bg }}>
          <td />
          <td colSpan={5} className="px-3 py-3">
            <p className="text-[11px] mb-1" style={{ color: C.slate }}>
              {money(residual)} of this credit is unallocated. Tick every invoice it settles and
              adjust the amounts — one credit can settle several invoices.
            </p>
            {cands.length === 0 && (
              <p className="text-[11px] mb-1" style={{ color: C.amber }}>
                Nothing in the narration names an open invoice. Search for it below or flag the credit.
              </p>
            )}
            {cands.map((c, i) => (
              <CandidateRow key={c.invoice_id} cand={c} idx={i} lineId={line.line_id}
                selected={sel[c.invoice_id] !== undefined}
                amount={sel[c.invoice_id] ?? ''}
                onToggle={() => toggle(c.invoice_id, c.suggested_amount)}
                onAmount={(v) => setSel(p => ({ ...p, [c.invoice_id]: v }))} />
            ))}
            <ManualSearch api={api} lineId={line.line_id} onPick={(inv) => {
              if (!cands.some(c => c.invoice_id === inv.invoice_id)) {
                setCands(p => [...p, { ...inv, confidence: 0, tier: 'manual', basis: ['chosen by hand'], suggested_amount: Math.min(residual, inv.due) }]);
              }
              setSel(p => ({ ...p, [inv.invoice_id]: String(Math.min(residual, inv.due).toFixed(2)) }));
            }} />
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button onClick={confirm} disabled={busy} data-testid={`bank-confirm-receipt-${line.line_id}`}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-sm text-white disabled:opacity-40 inline-flex items-center gap-1"
                style={{ background: C.green }}>
                <CheckCircle2 className="w-3 h-3" /> {busy ? 'Recording…' : 'Confirm receipt'}
              </button>
              {!flagging && line.status !== 'exception' && (
                <button onClick={() => setFlagging(true)} data-testid={`bank-flag-${line.line_id}`}
                  className="text-[11px] px-2 py-1.5 rounded-sm border inline-flex items-center gap-1"
                  style={{ borderColor: C.red + '55', color: C.red }}>
                  <Flag className="w-3 h-3" /> Flag for review
                </button>
              )}
              {flagging && (
                <span className="flex items-center gap-1">
                  <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why can't this be receipted?"
                    data-testid={`bank-flag-reason-${line.line_id}`}
                    className="text-[11px] px-2 py-1 rounded-sm border w-64" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
                  <button onClick={flag} disabled={busy} data-testid={`bank-flag-submit-${line.line_id}`}
                    className="text-[11px] font-semibold px-2 py-1.5 rounded-sm text-white" style={{ background: C.red }}>Flag</button>
                  <button onClick={() => setFlagging(false)} className="p-1"><X className="w-3 h-3" style={{ color: C.slate }} /></button>
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function BankReceiptingTab({ api }) {
  const [sum, setSum] = useState(null);
  const [statements, setStatements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => { setConfirmRemove(false); }, [selected]);

  const loadIndex = useCallback(async (pick) => {
    const [s, l] = await Promise.allSettled([
      api.get('/finance/bank/summary'), api.get('/finance/bank/statements'),
    ]);
    if (s.status === 'fulfilled') setSum(s.value.data);
    const rows = l.status === 'fulfilled' ? (l.value.data.statements || []) : [];
    setStatements(rows);
    setSelected(prev => pick || prev || (rows[0] || {}).statement_id || null);
    setLoading(false);
  }, [api]);

  useEffect(() => { loadIndex(); }, [loadIndex]);

  const loadDetail = useCallback(async () => {
    if (!selected) { setDetail(null); return; }
    try {
      const r = await api.get(`/finance/bank/statements/${selected}`);
      setDetail(r.data);
    } catch { setDetail(null); }
  }, [api, selected]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const refreshAll = () => { loadIndex(); loadDetail(); };

  const rematch = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finance/bank/statements/${selected}/rematch`);
      toast.success(`${r.data.lines_rematched} credits rematched against ${r.data.open_invoices} open invoices`);
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rematch failed'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/finance/bank/statements/${selected}`);
      toast.success('Statement removed');
      setSelected(null); setDetail(null); loadIndex();
    } catch (e) { toast.error(e?.response?.data?.detail || 'The statement stays on file'); }
    finally { setBusy(false); }
  };

  const exportCsv = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/finance/bank/statements/${selected}/export.csv`, '_blank');
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;

  return (
    <div className="space-y-4" data-testid="bank-receipting-tab">
      {sum && (
        <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.16)' }} data-testid="bank-summary">
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
            <Stat testid="bank-summary-ready" label="Ready to receipt" value={sum.ready_to_receipt.count}
              sub={money(sum.ready_to_receipt.amount)} color={C.blue} />
            <Stat testid="bank-summary-unmatched" label="No match" value={sum.unmatched.count}
              sub={money(sum.unmatched.amount)} color={C.amber} />
            <Stat testid="bank-summary-receipted" label="Receipted from files" value={sum.receipted.count}
              sub={money(sum.receipted.amount)} color={C.green} />
            <Stat testid="bank-summary-exceptions" label="Flagged" value={sum.exceptions.count}
              sub={money(sum.exceptions.amount)} color={C.red} />
            <Stat testid="bank-summary-openinv" label="Open invoices" value={sum.open_invoices.count}
              sub={`${money(sum.open_invoices.owed)} owed`} />
          </div>
        </Card>
      )}

      <UploadCard api={api} onLoaded={(id) => { setSelected(id); loadIndex(id); }} />

      {statements.length > 0 && (
        <Card className="p-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.16)' }}>
          <div className="flex flex-wrap items-center gap-2">
            {statements.map(s => (
              <button key={s.statement_id} onClick={() => setSelected(s.statement_id)}
                data-testid={`bank-statement-${s.statement_id}`}
                className="text-left px-3 py-2 rounded-sm border transition-colors"
                style={selected === s.statement_id
                  ? { borderColor: C.blue, background: C.blue + '0C' }
                  : { borderColor: 'rgba(136,150,168,0.22)' }}>
                <p className="text-[11px] font-medium flex items-center gap-1" style={{ color: C.navy }}>
                  <FileText className="w-3 h-3" style={{ color: C.slate }} />
                  {s.filename}
                </p>
                <p className="text-[10px]" style={{ color: C.slate }}>
                  {s.format.toUpperCase()} · {s.credit_count} credits · {s.date_from}
                  {s.date_to !== s.date_from ? ` → ${s.date_to}` : ''}
                  {s.open_lines > 0
                    ? <span style={{ color: C.amber }}> · {s.open_lines} open</span>
                    : <span style={{ color: C.green }}> · settled</span>}
                </p>
              </button>
            ))}
          </div>
        </Card>
      )}

      {detail && (
        <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.16)' }} data-testid="bank-statement-detail">
          <div className="px-4 py-3 flex flex-wrap items-center gap-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
            <div className="flex-1 min-w-[200px]">
              <h3 className="text-sm font-semibold" style={{ color: C.navy }}>{detail.statement.filename}</h3>
              <p className="text-[11px]" style={{ color: C.slate }}>
                {detail.statement.credit_count} credits totalling {money(detail.statement.credit_total)}
                {detail.statement.debit_count > 0 ? ` · ${detail.statement.debit_count} debits ignored` : ''}
                {detail.statement.duplicate_lines > 0 ? ` · ${detail.statement.duplicate_lines} already loaded elsewhere` : ''}
                {detail.statement.account ? ` · ${detail.statement.account}` : ''}
              </p>
            </div>
            <button onClick={rematch} disabled={busy} data-testid="bank-rematch-btn"
              className="text-[11px] px-2 py-1.5 rounded-sm border inline-flex items-center gap-1"
              style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
              <RefreshCw className="w-3 h-3" /> Rematch
            </button>
            <button onClick={exportCsv} data-testid="bank-export-btn"
              className="text-[11px] px-2 py-1.5 rounded-sm border inline-flex items-center gap-1"
              style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
              <Download className="w-3 h-3" /> CSV
            </button>
            <button onClick={() => confirmRemove ? remove() : setConfirmRemove(true)} disabled={busy}
              data-testid={confirmRemove ? 'bank-delete-confirm-btn' : 'bank-delete-statement-btn'}
              className="text-[11px] px-2 py-1.5 rounded-sm border inline-flex items-center gap-1"
              style={confirmRemove ? { background: C.red, color: '#fff', borderColor: C.red }
                : { borderColor: C.red + '55', color: C.red }}>
              <Trash2 className="w-3 h-3" /> {confirmRemove ? 'Confirm removal' : 'Remove'}
            </button>
          </div>
          {(detail.statement.warnings || []).map((w, i) => (
            <p key={i} className="px-4 py-2 text-[11px]" style={{ background: C.amber + '0C', color: C.amber }}>
              <AlertTriangle className="w-3 h-3 inline mr-1" />{w}
            </p>
          ))}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: C.bg }}>
                  {['Posted', 'Narration', 'Credit', 'Best match', 'Status', ''].map((h, i) => (
                    <th key={i} className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-left"
                      style={{ color: C.slate }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.lines.map(l => (
                  <LineRow key={l.line_id} api={api} line={l} onChanged={refreshAll} />
                ))}
              </tbody>
            </table>
          </div>
          {detail.lines.length === 0 && (
            <p className="p-4 text-[11px]" style={{ color: C.slate }}>No credits on this statement.</p>
          )}
        </Card>
      )}

      {statements.length === 0 && (
        <Card className="p-6 rounded-md text-center" style={{ border: '1px dashed rgba(136,150,168,0.3)' }} data-testid="bank-empty">
          <p className="text-[12px]" style={{ color: C.slate }}>
            No bank statement has been loaded yet. Export the account's transactions from the bank
            and load the file above — the credits will be matched against the
            {sum ? ` ${sum.open_invoices.count} ` : ' '}open invoices on the book.
          </p>
        </Card>
      )}
    </div>
  );
}
